import { SpaceviewerAxisType, } from "shared/types";
import { defaultHoverLabel } from "shared/plotly/defaultConfigs";
import { localizedNumber } from "shared/numberParser";
import { numberToFixedPointNotation, renderNumberValue } from "utils/string";
import { N_A } from "shared/rsDictionary";
import * as colors from "styles/colorValues.module.scss";
export var ColorMapType;
(function (ColorMapType) {
    ColorMapType["Low"] = "Low";
    ColorMapType["Middle"] = "Middle";
    ColorMapType["High"] = "High";
})(ColorMapType || (ColorMapType = {}));
export var SpaceviewerMode;
(function (SpaceviewerMode) {
    SpaceviewerMode["D1"] = "D1";
    SpaceviewerMode["D2"] = "D2";
    SpaceviewerMode["D3"] = "D3";
})(SpaceviewerMode || (SpaceviewerMode = {}));
const VISIBILITY_THRESHOLD = 0.1;
export const PROXIMITY_LEVELS = 10;
export const SLIDER_SCALE_SEGMENT = 20;
export const D1_HEIGHT = 120;
export const SPACEVIEWER_HEIGHT = 600;
export const PLOT_PADDING = 48;
export const CONFIDENCELESS_POINT_SIZE = 30;
export const DEFAULT_LABEL = "Source parameter is not selected";
export const INSIGHT_LABEL = "Insight value";
const { decimal, group } = localizedNumber.numberSeparators;
const defaultAxisConfig = {
    showgrid: false,
    showline: false,
    showticklabels: false,
    zeroline: false,
    fixedrange: true,
};
export const getAxisLimits = (axis) => (axis?.type === SpaceviewerAxisType.FLOAT
    ? [axis?.min ?? 0, axis?.max ?? 0]
    : [axis?.steps?.at(0)?.value ?? 0, axis?.steps?.at(-1)?.value ?? 0]) ?? [0, 0];
const getPaddingSize = (max = 0, min = 0, length = 1) => (PLOT_PADDING * (max - min)) / length;
export const getPlotLayout = (xAxis, yAxis, width, height = SPACEVIEWER_HEIGHT) => {
    const xLimits = getAxisLimits(xAxis);
    const yLimits = getAxisLimits(yAxis);
    const xPadding = getPaddingSize(xLimits[1], xLimits[0], width);
    const yPadding = getPaddingSize(yLimits[1], yLimits[0], height);
    return {
        autosize: true,
        dragmode: false,
        showlegend: false,
        hoverlabel: defaultHoverLabel,
        hovermode: "closest",
        margin: {
            l: 0,
            r: 0,
            b: 0,
            t: 0,
            pad: 0,
        },
        width: width ?? 0,
        height,
        separators: `${decimal}${group}`,
        xaxis: { ...defaultAxisConfig, range: [(xLimits[0] || 0) - xPadding, (xLimits[1] || 0) + xPadding] },
        yaxis: { ...defaultAxisConfig, range: [(yLimits[0] || 0) - yPadding, (yLimits[1] || 0) + yPadding] },
        plot_bgcolor: colors.black,
    };
};
export const get1DPlotLayout = (xAxis, width) => getPlotLayout(xAxis, { min: 0, max: D1_HEIGHT, type: SpaceviewerAxisType.FLOAT }, width, D1_HEIGHT);
export const POINT_COLORS = [
    colors.insightValue0,
    colors.insightValue1,
    colors.insightValue2,
    colors.insightValue3,
    colors.insightValue4,
    colors.insightValue5,
    colors.insightValue6,
    colors.insightValue7,
    colors.insightValue8,
    colors.insightValue9,
];
export const getPointColor = (value, order) => {
    if (typeof value === "string") {
        return colors.red;
    }
    const colorPoints = getPointsOrder(order);
    return colorPoints[value >= 100 ? colorPoints.length - 1 : Math.floor((value * colorPoints.length) / 100)];
};
export const getPointsOrder = (status) => {
    const pointColorsReversed = [...POINT_COLORS].reverse();
    switch (status) {
        case ColorMapType.Low:
            return pointColorsReversed;
        case ColorMapType.Middle:
            return [...POINT_COLORS, ...pointColorsReversed];
        case ColorMapType.High:
            return POINT_COLORS;
        default:
            return POINT_COLORS;
    }
};
export const prepareParameterColorMap = (axis, values) => {
    const limits = getAxisLimits(axis);
    const sliderScaleSegment = axis.type === SpaceviewerAxisType.INTEGER ? axis.steps.length : SLIDER_SCALE_SEGMENT;
    return values.reduce((acc, { values, response: { value } }) => {
        let colorMapIndex = Math.floor(((values[axis.index] - limits[0]) / (limits[1] - limits[0])) * sliderScaleSegment);
        if (colorMapIndex === sliderScaleSegment) {
            colorMapIndex--;
        }
        return {
            ...acc,
            [colorMapIndex]: Math.max(acc[colorMapIndex] || 0, numberToFixedPointNotation(value ?? 0, 6)),
        };
    }, {});
};
const prepareColorMapSegments = (insights, range, colorMapType) => Object.keys(insights).reduce((acc, index) => {
    acc[Number(index)] = getPointColor(getValueWithinRange(insights[Number(index)], range), colorMapType);
    return acc;
}, {});
export const getColorMap = (parameter, values, range, colorMapType) => prepareColorMapSegments(prepareParameterColorMap(parameter, values), range, colorMapType);
export const prepareInitialSlidersValues = (sliders, data) => {
    const slidersIndexes = sliders.map(({ index }) => index);
    let optimizedPointIndex = 0;
    let minValue = numberToFixedPointNotation(data[0]?.response?.value ?? 0, 6) || 0;
    let maxValue = numberToFixedPointNotation(data[0]?.response?.value ?? 0, 6) || 0;
    const coords = data.map(({ values, response: { value } }, pointIndex) => {
        const roundedValue = numberToFixedPointNotation(value ?? 0, 6);
        if (roundedValue > maxValue) {
            maxValue = roundedValue;
            optimizedPointIndex = pointIndex;
        }
        if (roundedValue < minValue) {
            minValue = roundedValue;
        }
        return slidersIndexes.reduce((sliderCoords, sliderIndex) => {
            if (sliderIndex === undefined) {
                return sliderCoords;
            }
            sliderCoords[sliderIndex] = values?.[sliderIndex];
            return sliderCoords;
        }, []);
    });
    return {
        sliderValues: sliders.reduce((acc, { index }) => {
            acc[index] = coords.map((sliderCoords) => sliderCoords[index]).sort((a, b) => a - b)[0];
            return acc;
        }, []),
        optimizedPoint: data[optimizedPointIndex]
            ? { index: optimizedPointIndex, data: data[optimizedPointIndex] }
            : undefined,
        range: [minValue, maxValue],
    };
};
export const prepare1DSpaceviewerData = (data, range, colorMapType, totalRunsCount, selectedPoint, calculatedSize) => {
    const points = data.map(({ values, response, errorCount }, pointIndex) => ({
        x: values[0],
        y: D1_HEIGHT / 2,
        value: response?.value === null ? N_A : numberToFixedPointNotation(response?.value, 6),
        confidence: response?.confidence,
        opacity: 1,
        index: pointIndex,
        errorCount,
    }));
    const preparedSelectedPoint = selectedPoint
        ? {
            x: selectedPoint.data?.values[0],
            y: D1_HEIGHT / 2,
            value: selectedPoint.data?.response?.value === null
                ? N_A
                : numberToFixedPointNotation(selectedPoint.data?.response?.value, 6),
            confidence: selectedPoint.data?.response?.confidence,
            opacity: 1,
            index: selectedPoint.index,
        }
        : undefined;
    return prepareGraphData(points, range, colorMapType, totalRunsCount, preparedSelectedPoint, calculatedSize);
};
export const prepare2DSpaceviewerData = (data, range, colorMapType, totalRunsCount, selectedPoint, calculatedSize) => {
    const points = data.map(({ values, response, errorCount }, pointIndex) => ({
        x: values[0],
        y: values[1],
        value: response?.value === null ? N_A : numberToFixedPointNotation(response?.value, 6),
        confidence: response?.confidence,
        opacity: 1,
        index: pointIndex,
        errorCount,
    }));
    const preparedSelectedPoint = selectedPoint
        ? {
            x: selectedPoint.data?.values[0],
            y: selectedPoint.data?.values[1],
            value: selectedPoint.data?.response?.value === null
                ? N_A
                : numberToFixedPointNotation(selectedPoint.data?.response?.value, 6),
            confidence: selectedPoint.data?.response?.confidence,
            opacity: 1,
            index: selectedPoint.index,
        }
        : undefined;
    return prepareGraphData(points, range, colorMapType, totalRunsCount, preparedSelectedPoint, calculatedSize);
};
export const prepareSpaceviewerData = (slidersValues, sliders, xAxisIndex = 0, yAxisIndex = 1, data, range, colorMapType, totalRunsCount, selectedPoint, calculatedSize) => {
    const points = data.reduce((acc, { values, response, errorCount }, pointIndex) => {
        const netDistance = sliders.reduce((distance, slider) => {
            const { index: realSliderIndex } = slider;
            const limits = getAxisLimits(slider);
            const slidersValue = slidersValues[realSliderIndex];
            if (slidersValue === undefined) {
                return distance;
            }
            const normCurrentDistance = (values[realSliderIndex] - slidersValue) / (limits[1] - limits[0]);
            return distance + normCurrentDistance * normCurrentDistance;
        }, 0);
        const distance = Math.sqrt(netDistance) / Math.sqrt(sliders.length);
        if (distance <= VISIBILITY_THRESHOLD) {
            acc.push({
                x: values[xAxisIndex],
                y: values[yAxisIndex],
                value: response?.value === null ? N_A : numberToFixedPointNotation(response?.value, 6),
                confidence: response.confidence,
                opacity: 1 - distance * 10,
                index: pointIndex,
                errorCount,
            });
        }
        return acc;
    }, []);
    const preparedSelectedPoint = points.length > 0 && selectedPoint
        ? {
            x: selectedPoint.data?.values[xAxisIndex],
            y: selectedPoint.data?.values[yAxisIndex],
            value: selectedPoint.data?.response?.value === null
                ? N_A
                : numberToFixedPointNotation(selectedPoint.data?.response?.value, 6),
            confidence: selectedPoint.data?.response?.confidence,
            opacity: 1,
            index: selectedPoint.index,
        }
        : undefined;
    return prepareGraphData(points, range, colorMapType, totalRunsCount, preparedSelectedPoint, calculatedSize);
};
export const getValueWithinRange = (value, range) => {
    if (typeof value === "string") {
        return N_A;
    }
    const rangeLength = range[1] - range[0];
    if (rangeLength === 0) {
        return 100;
    }
    return ((value - range[0]) * 100) / rangeLength;
};
const getPointSize = ({ confidence = 0, errorCount }, totalRunsCount) => {
    const sizeMin = 12;
    const sizeMax = 48;
    if (confidence === null && errorCount === totalRunsCount) {
        return sizeMax;
    }
    return (sizeMax - sizeMin) * (confidence / 100) + sizeMin;
};
export const prepareGraphData = (points, range, colorMapType, totalRunsCount, selectedPoint, calculatedSize) => {
    const LINE_WIDTH = 4;
    // TODO: those inner points.map() calls can be optimized if become a performance issue
    const mainTrace = {
        x: [],
        y: [],
        customdata: points.map(({ index }) => index),
        text: points.map(({ value }) => (typeof value === "string" ? N_A : `${renderNumberValue(value)}`)),
        hoverinfo: "text",
        mode: "markers",
        marker: {
            color: points.map(({ value }) => getPointColor(getValueWithinRange(value, range), colorMapType)),
            size: calculatedSize ? points.map(getPointSize) : CONFIDENCELESS_POINT_SIZE,
            opacity: points.map(({ opacity }) => opacity || 0),
            line: { color: "rgba(0, 0,0,0)" },
        },
    };
    const failedPoints = points.filter(({ errorCount = 0 }) => errorCount > 0);
    const errorsTrace = {
        x: [],
        y: [],
        hoverinfo: "skip",
        mode: "markers",
        marker: {
            color: failedPoints.map(({ value, errorCount }) => {
                const regularColor = getPointColor(getValueWithinRange(value, range), colorMapType);
                const allRunsFailed = errorCount === totalRunsCount && value === N_A;
                return allRunsFailed ? colors.red : regularColor;
            }),
            size: calculatedSize
                ? failedPoints.map((item) => {
                    const regularSize = getPointSize(item, totalRunsCount);
                    const isSelectedPoint = item.x === selectedPoint?.x && item.y === selectedPoint?.y;
                    return isSelectedPoint ? regularSize - LINE_WIDTH * 2 : regularSize;
                })
                : CONFIDENCELESS_POINT_SIZE - LINE_WIDTH * 2,
            opacity: failedPoints.map(({ opacity }) => opacity || 0),
            line: { color: colors.red, width: LINE_WIDTH },
        },
    };
    points.forEach(({ x, y, errorCount = 0 }) => {
        mainTrace.x.push(x);
        mainTrace.y.push(y);
        if (errorCount > 0) {
            errorsTrace.x.push(x);
            errorsTrace.y.push(y);
        }
    });
    const selectedPointTrace = selectedPoint
        ? {
            x: [selectedPoint.x],
            y: [selectedPoint.y],
            customdata: selectedPoint.index,
            text: typeof selectedPoint.value === "string" ? N_A : `${renderNumberValue(selectedPoint.value)}`,
            hoverinfo: "skip",
            mode: "markers",
            marker: {
                color: getPointColor(getValueWithinRange(selectedPoint.value, range), colorMapType),
                size: calculatedSize ? getPointSize(selectedPoint) : CONFIDENCELESS_POINT_SIZE,
                opacity: selectedPoint.opacity || 0,
                line: { color: colors.white, width: LINE_WIDTH },
            },
        }
        : {};
    return [mainTrace, selectedPointTrace, errorsTrace];
};
export const changeAxis = (sliders, index) => sliders.reduce((acc, slider) => {
    if (index === slider.index) {
        acc.newAxis = slider;
    }
    else {
        acc.sliders.push(slider);
    }
    return acc;
}, { sliders: [], newAxis: {} });
export const getIndexedParameters = (dataParameters = [], legendParameters = []) => dataParameters.map((parameter, index) => {
    const { source = "", unit = "" } = legendParameters.find((item) => `${parameter.type === SpaceviewerAxisType.FLOAT ? item?.parameterId : item?.stepId}` ===
        `${parameter.id}` && item?.inputId === parameter.sourceId) || {};
    return {
        ...parameter,
        index,
        colorMap: {},
        source,
        unit,
    };
});
